import React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '../../lib/utils';

interface SliderProps {
    value: number[];
    onValueChange: (value: number[]) => void;
    onValueCommit?: (value: number[]) => void;
    min?: number;
    max?: number;
    step?: number;
    label?: string;
    showValue?: boolean;
    formatValue?: (value: number) => string;
    className?: string;
    disabled?: boolean;
}

export const Slider: React.FC<SliderProps> = ({
    value,
    onValueChange,
    onValueCommit,
    min = 0,
    max = 100,
    step = 1,
    label,
    showValue = true,
    formatValue = (v) => v.toString(),
    className,
    disabled = false,
}) => {
    return (
        <div className={cn('space-y-2', className)}>
            {(label || showValue) && (
                <div className="flex items-center justify-between text-sm">
                    {label && <span className="text-surface-600 font-medium">{label}</span>}
                    {showValue && (
                        <span className="text-primary-600 font-mono text-xs bg-primary-50 px-2 py-0.5 rounded">
                            {value.map(formatValue).join(' - ')}
                        </span>
                    )}
                </div>
            )}
            <SliderPrimitive.Root
                className={cn(
                    'relative flex items-center select-none touch-none w-full h-5 group',
                    disabled && 'opacity-50 cursor-not-allowed'
                )}
                value={value}
                onValueChange={onValueChange}
                onValueCommit={onValueCommit}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
            >
                <SliderPrimitive.Track className="bg-surface-200 relative grow rounded-full h-2 overflow-hidden">
                    <SliderPrimitive.Range className="absolute h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full" />
                </SliderPrimitive.Track>
                {value.map((_, i) => (
                    <SliderPrimitive.Thumb
                        key={i}
                        className={cn(
                            'block w-5 h-5 bg-white rounded-full shadow-lg border-2 border-primary-500',
                            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                            'transition-all duration-150 hover:scale-110 hover:shadow-glow',
                            'cursor-grab active:cursor-grabbing'
                        )}
                    />
                ))}
            </SliderPrimitive.Root>
        </div>
    );
};

interface RangeSliderProps {
    value: [number, number];
    onValueChange: (value: [number, number]) => void;
    onValueCommit?: (value: [number, number]) => void;
    min?: number;
    max?: number;
    step?: number;
    label?: string;
    showValue?: boolean;
    formatValue?: (value: number) => string;
    className?: string;
    disabled?: boolean;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
    value,
    onValueChange,
    onValueCommit,
    ...props
}) => {
    return (
        <Slider
            value={value}
            onValueChange={(v) => onValueChange([v[0], v[1]])}
            onValueCommit={onValueCommit ? (v) => onValueCommit([v[0], v[1]]) : undefined}
            {...props}
        />
    );
};
